/**
 * ROI Summary — pulls everything from the ledger. No synthetic projections.
 * Shows: last 12 months performance, CapEx register summary, payback, bank balance.
 * Requires business.ledgerEntityId to be set.
 */
import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { ExternalLink, AlertTriangle, RefreshCw } from 'lucide-react';
import ledgerApi from '../lib/ledgerApi';

function fmt(n, ccy = '') {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  return `${sign}${ccy} ${abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`.trim();
}

function fmtPct(n) {
  if (n === null || n === undefined || isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
}

export default function RoiSummary({ business }) {
  const entityId = business.ledgerEntityId;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);

  const refresh = async () => {
    if (!entityId) {
      setErr('No ledger entity linked. Set "Ledger entity ID" in Settings.');
      setLoading(false);
      return;
    }
    setLoading(true); setErr(null);
    try {
      const [s, t] = await Promise.all([
        ledgerApi.roiSummary(entityId),
        ledgerApi.monthlyTrend(entityId, 12),
      ]);
      setSummary(s); setTrend(t || []);
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [entityId]);

  if (!entityId) {
    return (
      <div className="card" style={{ padding: 20, marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#a83238' }}>
          <AlertTriangle size={18} /><strong>Not linked to a ledger entity.</strong>
        </div>
        <p style={{ marginTop: 10, color: '#6b6b6b' }}>
          Open Settings and set the <code>Ledger entity ID</code> (e.g. <code>kng</code>, <code>rr</code>) to enable this view.
        </p>
      </div>
    );
  }

  if (loading) return <div style={{ color: '#6b6b6b', padding: 20 }}>Loading from ledger…</div>;
  if (err) return <div style={{ padding: 10, background: '#fdeaea', color: '#a83238', borderRadius: 6 }}>Error: {err}</div>;
  if (!summary) return null;

  const ccy = summary.entity.base_currency;
  const { ltm, capex, bank_balance, payback_months, payback_years, avg_monthly_net } = summary;

  const trendForChart = trend.map((r) => ({
    ym: r.ym,
    Revenue: r.revenue,
    Expenses: r.expenses,
    Net: r.net,
  }));

  const kpis = [
    { label: 'LTM Revenue', value: fmt(ltm.revenue, ccy) },
    { label: 'LTM Net Profit', value: fmt(ltm.net_profit, ccy), cls: ltm.net_profit >= 0 ? 'positive' : 'negative' },
    { label: 'Net Margin', value: fmtPct(ltm.net_margin_pct), cls: ltm.net_margin_pct >= 0 ? 'positive' : 'negative' },
    { label: 'Gross Margin', value: fmtPct(ltm.gross_margin_pct) },
    { label: 'Total CapEx', value: fmt(capex.total_acquisition_cost, ccy) },
    { label: 'Net Book Value', value: fmt(capex.net_book_value, ccy) },
    { label: 'Bank Balance', value: fmt(bank_balance, ccy) },
    { label: 'Payback', value: payback_years ? `${payback_years.toFixed(1)} yrs` : 'N/A (no positive net)' },
  ];

  return (
    <div className="roi-summary">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: '#6b6b6b', fontSize: 13 }}>
          Last 12 months performance pulled live from <code>ledger.nexkoins.com</code>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-sm" onClick={refresh}><RefreshCw size={14} /></button>
          <button className="btn btn-sm" onClick={() => window.open(ledgerApi.deepLink(entityId, 'pnl'), '_blank')}>
            <ExternalLink size={14} /> Open P&L in Ledger
          </button>
        </div>
      </div>

      {/* KPI grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 20 }}>
        {kpis.map((k) => (
          <div key={k.label} className="card" style={{ padding: 14 }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#6b6b6b', letterSpacing: 0.4 }}>{k.label}</div>
            <div className={k.cls || ''} style={{ fontSize: 20, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Trend chart */}
      <h3 style={{ margin: '12px 0', fontSize: 14, textTransform: 'uppercase', color: '#6b6b6b' }}>12-Month Trend</h3>
      <div style={{ height: 300, background: '#fff', padding: 12, border: '1px solid #e4e4e2', borderRadius: 6 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={trendForChart}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="ym" />
            <YAxis />
            <Tooltip formatter={(v) => fmt(v, ccy)} />
            <Legend />
            <Bar dataKey="Revenue" fill="#7a8b8f" />
            <Bar dataKey="Expenses" fill="#a83238" />
            <Bar dataKey="Net" fill="#b6934a" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly table */}
      <h3 style={{ margin: '20px 0 10px', fontSize: 14, textTransform: 'uppercase', color: '#6b6b6b' }}>Monthly Breakdown</h3>
      <div className="nk-table-scroll" style={{ border: '1px solid #e4e4e2', borderRadius: 6 }}>
        <table className="nk-table-cards" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#fafaf8' }}>
            <tr>
              <th style={{ padding: 10, textAlign: 'left' }}>Month</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Revenue</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Expenses</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Net</th>
            </tr>
          </thead>
          <tbody>
            {trend.map((r) => (
              <tr key={r.ym} style={{ borderTop: '1px solid #e4e4e2' }}>
                <td data-label="Month" style={{ padding: 10 }}>{r.ym}</td>
                <td data-label="Revenue" style={{ padding: 10, textAlign: 'right' }}>{fmt(r.revenue, ccy)}</td>
                <td data-label="Expenses" style={{ padding: 10, textAlign: 'right' }}>{fmt(r.expenses, ccy)}</td>
                <td data-label="Net" style={{ padding: 10, textAlign: 'right', color: r.net >= 0 ? '#1f7a4d' : '#a83238', fontWeight: 600 }}>{fmt(r.net, ccy)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CapEx footer */}
      <h3 style={{ margin: '20px 0 10px', fontSize: 14, textTransform: 'uppercase', color: '#6b6b6b' }}>CapEx Summary</h3>
      <div style={{ padding: 14, background: '#fff', border: '1px solid #e4e4e2', borderRadius: 6, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        <div><div style={{ fontSize: 11, color: '#6b6b6b' }}>TOTAL ASSETS</div><div style={{ fontSize: 16, fontWeight: 700 }}>{capex.total_assets} ({capex.active_assets} active)</div></div>
        <div><div style={{ fontSize: 11, color: '#6b6b6b' }}>ACTIVE ACQUISITION COST</div><div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(capex.active_cost, ccy)}</div></div>
        <div><div style={{ fontSize: 11, color: '#6b6b6b' }}>ACCUMULATED DEPRECIATION</div><div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(capex.accumulated_depreciation, ccy)}</div></div>
        <div><div style={{ fontSize: 11, color: '#6b6b6b' }}>NET BOOK VALUE</div><div style={{ fontSize: 16, fontWeight: 700 }}>{fmt(capex.net_book_value, ccy)}</div></div>
      </div>
      <p style={{ fontSize: 11, color: '#6b6b6b', marginTop: 8 }}>
        Payback calculation: Total CapEx ({fmt(capex.total_acquisition_cost, ccy)}) ÷ avg monthly net ({fmt(avg_monthly_net, ccy)}).
        {payback_years === null && ' Requires positive net profit to estimate.'}
      </p>
    </div>
  );
}
